import React from "react";
import { Link } from "react-router-dom";
import { Check, X, Phone, Mail, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const LAST_UPDATED = "February 10, 2026";

const RefundPolicy = () => {
  useDocumentMeta({
    title: "Refund Policy — 30-Day Money-Back Guarantee",
    description:
      "ProsoftMarketplace offers a 30-day money-back guarantee on every antivirus license. Simple, no-questions-asked refunds. Call +1 (888) 655-4464.",
  });

  return (
    <div data-testid="refund-page" className="bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Legal</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            30-Day Money-Back Guarantee
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            If your antivirus license is <span className="font-bold text-slate-900">unused</span> and you request a refund
            within <span className="font-bold text-slate-900">30 days</span> of purchase, we&apos;ll issue a full refund —
            no questions asked.
          </p>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>

          <div
            data-testid="refund-key-rule"
            className="mt-6 inline-flex items-start gap-3 border-2 border-slate-900 bg-emerald-50 px-4 py-3 text-sm shadow-[6px_6px_0_0_rgba(16,185,129,1)]"
          >
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={3} />
            <div>
              <span className="font-display text-base font-bold text-slate-900">
                Refunds are issued for unused licenses only, within 30 days of purchase.
              </span>
              <p className="mt-1 text-slate-700">
                An &quot;unused&quot; license is one where the activation key has not been redeemed
                against your Huntheal account. Once the key is activated, it&apos;s considered used
                and is no longer refundable.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        {/* What's covered / not */}
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="border-2 border-slate-900 bg-white p-6">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
              <Check className="h-4 w-4" /> Eligible
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                "Refund requested within 30 days of purchase",
                "Activation key is UNUSED — not yet redeemed at huntheal.com",
                "Annual subscriptions purchased directly from prosoftmarketplace.com",
                "Bulk team purchases (5+ seats) — within 14 days of purchase, all keys unused",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={3} />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              <X className="h-4 w-4" /> Not eligible
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                "Activation key has already been redeemed/used — used licenses are non-refundable",
                "Purchases made more than 30 days ago (or 14 days for bulk orders)",
                "Auto-renewals — manage those in your vendor account dashboard",
                "Monthly subscriptions (if/when offered)",
                "Promotional bundles that explicitly state \"non-refundable\" at checkout",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={3} />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* How to request */}
        <div className="mt-10 border border-slate-200 bg-white p-6">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            How to request a refund
          </h2>
          <ol className="mt-4 space-y-4">
            {[
              "Find your order reference. It starts with NRT- and is shown on the success page and in your confirmation email.",
              "Email support@prosoftmarketplace.com or call +1 (888) 655-4464 with your order reference and a short reason (optional but helps us improve).",
              "We review the request within 1 business day. Approved refunds are processed to your original PayPal / card within 3–5 business days.",
              "Your activation key will be deactivated once the refund is processed.",
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-slate-900 font-display text-sm font-black text-white">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-700">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Processing times */}
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          <Stat label="Approval" value="< 24 hrs" />
          <Stat label="PayPal refund" value="3–5 days" />
          <Stat label="Cards via PayPal" value="5–10 days" />
        </div>

        {/* CTA */}
        <div className="mt-10 border-2 border-slate-900 bg-white p-6 shadow-[8px_8px_0_0_rgba(16,185,129,1)] sm:flex sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
              Need a refund?
            </div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter text-slate-900">
              We&apos;ll make it right.
            </h2>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:mt-0 sm:flex-row">
            <Button asChild data-testid="refund-call-btn" className="rounded-sm bg-slate-900 text-white hover:bg-slate-800">
              <a href="tel:+18886554464">
                <Phone className="mr-1 h-4 w-4" /> Call +1 (888) 655-4464
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="refund-email-btn"
              className="rounded-sm border-slate-300 hover:border-slate-900"
            >
              <a href="mailto:support@prosoftmarketplace.com">
                <Mail className="mr-1 h-4 w-4" /> Email support
              </a>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="rounded-sm font-bold uppercase tracking-[0.15em] hover:bg-slate-100"
            >
              <Link to="/contact">
                Contact form <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="border border-slate-200 bg-white p-4">
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="mt-1 font-display text-2xl font-black tracking-tighter text-slate-900">{value}</div>
  </div>
);

export default RefundPolicy;
